/**
 * US 14.1 — time on a task.
 * - ``TaskTimerButton``: start/stop in the task header. The timer lives on the
 *   server, so it keeps running across screens, tabs and devices, and
 *   starting it on another task stops and saves the first.
 * - ``TaskTimeSection``: "1h 20m of 3h", the estimate (plus subtasks'),
 *   entries, and logging time by hand. Only my own entries can be changed.
 * - ``TopbarTimer``: the small running timer shown while it runs.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { Lock, Pencil, Play, Plus, Square, Timer as TimerIcon, Trash2 } from "lucide-react";
import { cn } from "@weco/ui";
import { taskApi, type TaskApiError } from "../task/api";
import { SectionTitle } from "../task/sections";
import { formatDuration, parseDuration, reportingApi, useRefreshTime, useTimer, type TimeEntry } from "./api";

const fail = (err: unknown, fallback: string) => toast.error((err as TaskApiError)?.message || fallback);

export function Elapsed({ since }: { since: string }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const s = Math.max(0, Math.floor((Date.now() - new Date(since).getTime()) / 1000));
  const p = (n: number) => String(n).padStart(2, "0");
  return <span className="font-mono tabular-nums">{`${Math.floor(s / 3600)}:${p(Math.floor(s / 60) % 60)}:${p(s % 60)}`}</span>;
}

export function useTimerActions() {
  const { t } = useTranslation();
  const refresh = useRefreshTime();
  const failed = t("tasks.hubs.actionFailed", { defaultValue: "Couldn't save your change. Try again." });
  return {
    start: (taskId: string, title: string) =>
      reportingApi.start(taskId).then(
        (r) => {
          refresh(taskId);
          if (r.stopped) {
            refresh(r.stopped.task.id);
            toast.success(t("tasks.time.switched", { from: r.stopped.task.title, minutes: formatDuration(r.stopped.minutes), to: title, defaultValue: "Logged {{minutes}} on {{from}} · timer started on {{to}}" }));
          } else toast.success(t("tasks.time.started", { title, defaultValue: "Timer started on {{title}}" }));
        },
        (err) => fail(err, failed),
      ),
    stop: () =>
      reportingApi.stop().then(
        (r) => {
          refresh(r.task.id);
          toast.success(r.entry ? t("tasks.time.logged", { minutes: formatDuration(r.minutes), title: r.task.title, defaultValue: "Logged {{minutes}} on {{title}}" }) : t("tasks.time.underMinute", { defaultValue: "Timer stopped · under a minute, nothing logged" }));
        },
        (err) => fail(err, failed),
      ),
  };
}

export function TaskTimerButton({ taskId, title, canLog }: { taskId: string; title: string; canLog: boolean }) {
  const { t } = useTranslation();
  const timer = useTimer().data;
  const act = useTimerActions();
  const running = timer?.task.id === taskId;
  if (!canLog && !running) return null;
  return (
    <button
      type="button"
      onClick={() => void (running ? act.stop() : act.start(taskId, title))}
      aria-pressed={running}
      aria-label={running ? t("tasks.time.stop", { defaultValue: "Stop timer" }) : t("tasks.time.start", { defaultValue: "Start timer" })}
      title={running ? t("tasks.time.stop", { defaultValue: "Stop timer" }) : t("tasks.time.start", { defaultValue: "Start timer" })}
      className={cn("h-7 px-2 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors", running ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted")}
    >
      {running ? <Square className="size-3" /> : <Play className="size-3" />}
      {running ? <Elapsed since={timer!.started_at} /> : t("tasks.time.start", { defaultValue: "Start timer" })}
    </button>
  );
}

function EntryRow({ entry, onChanged }: { entry: TimeEntry; onChanged: () => void }) {
  const { t, i18n } = useTranslation();
  const [edit, setEdit] = useState<{ dur: string; date: string; note: string; err: string } | null>(null);
  const failed = t("tasks.hubs.actionFailed", { defaultValue: "Couldn't save your change. Try again." });
  const save = () => {
    if (!edit) return;
    const minutes = parseDuration(edit.dur);
    if (!(minutes >= 1)) return setEdit({ ...edit, err: t("tasks.time.badDuration", { defaultValue: "Use a format like 45m, 2h or 1h 30m." }) });
    reportingApi.editTime(entry.id, { minutes, date: edit.date, note: edit.note }).then(
      () => {
        setEdit(null);
        onChanged();
      },
      (err) => setEdit({ ...edit, err: (err as TaskApiError)?.message || failed }),
    );
  };
  if (edit)
    return (
      <li className="p-3 space-y-2">
        <div className="flex flex-wrap gap-2">
          <input autoFocus value={edit.dur} onChange={(e) => setEdit({ ...edit, dur: e.target.value, err: "" })} aria-label={t("tasks.time.duration", { defaultValue: "Duration" })} className="h-9 w-28 px-3 rounded-xl border border-border bg-background text-sm font-mono text-foreground outline-none focus:border-primary" />
          <input type="date" value={edit.date} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setEdit({ ...edit, date: e.target.value, err: "" })} aria-label={t("tasks.time.date", { defaultValue: "Date" })} className="h-9 px-3 rounded-xl border border-border bg-background text-sm text-foreground outline-none focus:border-primary" />
          <input value={edit.note} maxLength={200} onChange={(e) => setEdit({ ...edit, note: e.target.value })} aria-label={t("tasks.time.note", { defaultValue: "Note" })} placeholder={t("tasks.time.note", { defaultValue: "Note" })} className="h-9 flex-1 min-w-[160px] px-3 rounded-xl border border-border bg-background text-sm text-foreground outline-none focus:border-primary" />
        </div>
        {edit.err && <p role="alert" className="text-xs font-semibold text-red-600">{edit.err}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => setEdit(null)} className="h-9 px-3 rounded-xl border border-border bg-background/70 text-sm font-semibold hover:bg-muted">{t("tasks.cancel", { defaultValue: "Cancel" })}</button>
          <button type="button" onClick={save} className="h-9 px-3 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90">{t("tasks.time.save", { defaultValue: "Save" })}</button>
        </div>
      </li>
    );
  return (
    <li className="flex items-center gap-2 py-1.5 text-sm">
      <span className="flex-1 min-w-0">
        <span className="block truncate">
          {entry.name}
          <span className="text-muted-foreground">
            {" · "}
            {new Date(entry.started_at).toLocaleDateString(i18n.language, { month: "short", day: "numeric" })}
            {entry.source === "timer" ? ` · ${t("tasks.time.fromTimer", { defaultValue: "Timer" })}` : ""}
            {entry.edited ? ` · ${t("tasks.time.edited", { defaultValue: "Edited" })}` : ""}
          </span>
        </span>
        {entry.note && <span className="block text-xs text-muted-foreground truncate">{entry.note}</span>}
      </span>
      <span className="font-mono text-sm font-semibold tabular-nums">{formatDuration(entry.minutes)}</span>
      {entry.mine ? (
        <span className="flex">
          <button type="button" onClick={() => setEdit({ dur: formatDuration(entry.minutes), date: entry.started_at.slice(0, 10), note: entry.note, err: "" })} aria-label={t("tasks.time.editEntry", { defaultValue: "Edit entry" })} className="w-9 h-9 rounded-xl flex items-center justify-center text-muted-foreground hover:bg-muted">
            <Pencil className="size-3.5" />
          </button>
          <button type="button" onClick={() => void reportingApi.deleteTime(entry.id).then(onChanged, (err) => fail(err, failed))} aria-label={t("tasks.time.deleteEntry", { defaultValue: "Delete entry" })} className="w-9 h-9 rounded-xl flex items-center justify-center text-muted-foreground hover:bg-red-500/10 hover:text-red-600">
            <Trash2 className="size-3.5" />
          </button>
        </span>
      ) : (
        <span className="w-[72px] flex justify-center" title={t("tasks.time.onlyOwn", { defaultValue: "Only the person who logged it can change it" })}>
          <Lock className="size-3 text-muted-foreground" />
        </span>
      )}
    </li>
  );
}

export function TaskTimeSection({ taskId, estimate, canEdit, onChanged }: { taskId: string; estimate: number | null; canEdit: boolean; onChanged: () => void }) {
  const { t } = useTranslation();
  const q = useQuery({ queryKey: ["tasks", "task-time", taskId], queryFn: () => reportingApi.taskTime(taskId) });
  const refresh = useRefreshTime();
  const [estEdit, setEstEdit] = useState<string | null>(null);
  const [estErr, setEstErr] = useState("");
  const [adding, setAdding] = useState<{ dur: string; date: string; note: string; err: string } | null>(null);
  const data = q.data;
  const reload = () => {
    void q.refetch();
    refresh(taskId);
    onChanged();
  };
  const saveEstimate = () => {
    if (estEdit === null) return;
    const s = estEdit.trim();
    const minutes = s ? parseDuration(s) : null;
    if (s && !(minutes! >= 1)) return setEstErr(t("tasks.time.badDuration", { defaultValue: "Use a format like 45m, 2h or 1h 30m." }));
    taskApi.update(taskId, { time_estimate: minutes }).then(
      () => {
        setEstEdit(null);
        setEstErr("");
        reload();
      },
      (err) => setEstErr((err as TaskApiError)?.message || ""),
    );
  };
  const add = () => {
    if (!adding) return;
    const minutes = parseDuration(adding.dur);
    if (!(minutes >= 1)) return setAdding({ ...adding, err: t("tasks.time.badDuration", { defaultValue: "Use a format like 45m, 2h or 1h 30m." }) });
    reportingApi.addTime(taskId, { minutes, date: adding.date, note: adding.note }).then(
      () => {
        setAdding(null);
        reload();
      },
      (err) => setAdding({ ...adding, err: (err as TaskApiError)?.message || "" }),
    );
  };
  const total = data?.total ?? 0;
  const est = data?.estimate ?? estimate;
  const subEst = data?.subtask_estimate ?? 0;
  return (
    <section className="px-5 py-4 border-b border-border" aria-label={t("tasks.time.title", { defaultValue: "Time" })}>
      <SectionTitle icon={TimerIcon}>{t("tasks.time.title", { defaultValue: "Time" })}</SectionTitle>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-mono font-semibold" data-testid="time-logged">
          {est ? t("tasks.time.ofEstimate", { logged: formatDuration(total), estimate: formatDuration(est), defaultValue: "{{logged}} of {{estimate}}" }) : formatDuration(total)}
        </span>
        {est != null && est > 0 && (
          <span className="h-1.5 w-24 rounded-full bg-muted overflow-hidden" aria-hidden>
            <span className={cn("block h-full rounded-full", total > est ? "bg-red-500" : "bg-primary")} style={{ width: `${Math.min(100, (100 * total) / est)}%` }} />
          </span>
        )}
        {estEdit !== null ? (
          <input
            autoFocus
            value={estEdit}
            onChange={(e) => {
              setEstEdit(e.target.value);
              setEstErr("");
            }}
            onBlur={saveEstimate}
            onKeyDown={(e) => {
              if (e.key === "Enter") saveEstimate();
              if (e.key === "Escape") setEstEdit(null);
            }}
            placeholder="2h 30m"
            aria-label={t("tasks.time.estimate", { defaultValue: "Estimate" })}
            aria-invalid={!!estErr}
            className="h-7 w-24 px-2 rounded-lg border border-border bg-background text-xs font-mono"
          />
        ) : (
          <button type="button" disabled={!canEdit} onClick={() => setEstEdit(est ? formatDuration(est) : "")} className="h-7 px-2 rounded-lg text-xs font-mono text-muted-foreground hover:bg-muted disabled:hover:bg-transparent">
            {est ? t("tasks.time.estShort", { value: formatDuration(est), defaultValue: "{{value}} est." }) : t("tasks.time.addEstimate", { defaultValue: "Add estimate" })}
          </button>
        )}
        {subEst > 0 && <span className="text-xs text-muted-foreground">{t("tasks.time.subtaskEstimate", { value: formatDuration(subEst), defaultValue: "+ {{value}} in subtasks" })}</span>}
      </div>
      {estErr && <p role="alert" className="mt-1 text-xs text-red-600">{estErr}</p>}
      {data && data.entries.length > 0 && (
        <ul className="mt-2 divide-y divide-border">
          {data.entries.map((e) => (
            <EntryRow key={e.id} entry={e} onChanged={reload} />
          ))}
        </ul>
      )}
      {data?.can_log &&
        (adding ? (
          <div className="mt-2 space-y-2">
            <div className="flex flex-wrap gap-2">
              <input autoFocus value={adding.dur} onChange={(e) => setAdding({ ...adding, dur: e.target.value, err: "" })} placeholder="1h 30m" aria-label={t("tasks.time.duration", { defaultValue: "Duration" })} className="h-9 w-28 px-3 rounded-xl border border-border bg-background text-sm font-mono text-foreground outline-none focus:border-primary" />
              <input type="date" value={adding.date} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setAdding({ ...adding, date: e.target.value })} aria-label={t("tasks.time.date", { defaultValue: "Date" })} className="h-9 px-3 rounded-xl border border-border bg-background text-sm text-foreground outline-none focus:border-primary" />
              <input value={adding.note} maxLength={200} onChange={(e) => setAdding({ ...adding, note: e.target.value })} placeholder={t("tasks.time.note", { defaultValue: "Note" })} aria-label={t("tasks.time.note", { defaultValue: "Note" })} className="h-9 flex-1 min-w-[160px] px-3 rounded-xl border border-border bg-background text-sm text-foreground outline-none focus:border-primary" />
            </div>
            {adding.err && <p role="alert" className="text-xs font-semibold text-red-600">{adding.err}</p>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setAdding(null)} className="h-9 px-3 rounded-xl border border-border bg-background/70 text-sm font-semibold hover:bg-muted">{t("tasks.cancel", { defaultValue: "Cancel" })}</button>
              <button type="button" onClick={add} className="h-9 px-3 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90">{t("tasks.time.addTime", { defaultValue: "Add time" })}</button>
            </div>
          </div>
        ) : (
          <button type="button" onClick={() => setAdding({ dur: "", date: new Date().toISOString().slice(0, 10), note: "", err: "" })} className="mt-2 text-xs font-bold text-primary hover:underline flex items-center gap-1">
            <Plus className="size-3" /> {t("tasks.time.logTime", { defaultValue: "Log time" })}
          </button>
        ))}
    </section>
  );
}

/** The small running timer in the top bar; opens its task, stops it. */
export function TopbarTimer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const timer = useTimer().data;
  const act = useTimerActions();
  if (!timer) return null;
  return (
    <div role="status" className="fixed top-2.5 start-1/2 -translate-x-1/2 rtl:translate-x-1/2 z-[60] flex items-center gap-1 h-8 ps-2.5 pe-1 rounded-full bg-primary text-primary-foreground text-xs font-semibold shadow-lg">
      <span className="size-2 rounded-full bg-primary-foreground/80 animate-pulse" aria-hidden />
      <button type="button" onClick={() => navigate(`/tasks/t/${timer.task.id}`)} className="max-w-40 truncate hover:underline" title={timer.task.title}>
        {timer.task.key || timer.task.title}
      </button>
      <Elapsed since={timer.started_at} />
      <button type="button" onClick={() => void act.stop()} aria-label={t("tasks.time.stop", { defaultValue: "Stop timer" })} className="w-6 h-6 rounded-full flex items-center justify-center hover:bg-primary-foreground/15">
        <Square className="size-3" />
      </button>
    </div>
  );
}
